import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import React, { useState } from 'react';
import { UseMutateAsyncFunction } from 'react-query';
import { DownloadButton } from 'src/components/Download/DownloadButton';
import { WretcherResponse } from 'wretch';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ConfirmationDialog from 'src/components/ConfirmationDialog/ConfirmationDialog';
import LabeledField from 'src/components/Form/LabeledField';
import TextField from '@mui/material/TextField';
import CalculateIcon from '@mui/icons-material/Calculate';

interface AcopMaintenanceButtonsProps {
  downloadMutateAsync: UseMutateAsyncFunction<
    WretcherResponse,
    unknown,
    void,
    unknown
  >;
  handleAcopDelete: (action: string, reason: string) => void;
  isDelinkDisabled: boolean;
  isDownloadDisabled: boolean;
}

const AcopMaintenanceButtons: React.FC<AcopMaintenanceButtonsProps> = ({
  downloadMutateAsync,
  handleAcopDelete,
  isDelinkDisabled,
  isDownloadDisabled,
}) => {
  const translate = useTranslate();

  const [openAcopDeleteConfirmation, setOpenAcopDeleteConfirmation] = useState<
    'DELINK' | 'DELINK_RECOST' | false
  >(false);
  const [reason, setReason] = useState<string>('');

  return (
    <Box>
      <Stack flexDirection={'row'} gap={2} justifyContent={'flex-end'}>
        <DownloadButton
          disabled={isDownloadDisabled}
          fileName="ACOPDetailsFile.xlsx"
          mutateAsync={downloadMutateAsync}
        />
        <Button
          disabled={isDelinkDisabled}
          onClick={() => {
            setOpenAcopDeleteConfirmation('DELINK');
          }}
          startIcon={<LinkOffIcon />}
          variant="newContained"
        >
          {translate('Common.Delink')}
        </Button>
        {
          <Button
            disabled={isDelinkDisabled}
            endIcon={<CalculateIcon />}
            onClick={() => {
              setOpenAcopDeleteConfirmation('DELINK_RECOST');
            }}
            startIcon={<LinkOffIcon />}
            variant="newContained"
          >
            {translate('Common.DelinkAndRecost')}
          </Button>
        }
      </Stack>
      {openAcopDeleteConfirmation && (
        <ConfirmationDialog
          buttons={[
            {
              children: translate('Common.No'),
              onClick: () => {
                setOpenAcopDeleteConfirmation(false);
                setReason('');
              },
              variant: 'newOutlined',
            },
            {
              children: translate('Common.Yes'),
              disabled: !reason,
              onClick: () => {
                setOpenAcopDeleteConfirmation(false);
                setReason('');
                handleAcopDelete(openAcopDeleteConfirmation, reason);
              },
              variant: 'newContained',
            },
          ]}
          content={
            <LabeledField isRequired label={translate('Common.ReasonLabel')}>
              <TextField
                data-testid="gds-acop-delete-reason"
                fullWidth
                maxRows={4}
                minRows={4}
                multiline
                onChange={(e) => setReason(e.target.value)}
                placeholder={translate('Common.PlaceholderEnterValue')}
              />
            </LabeledField>
          }
          open={Boolean(openAcopDeleteConfirmation)}
          title={translate('AcopMaintenance.DeleteConfirmationTitle')}
        />
      )}
    </Box>
  );
};

export default AcopMaintenanceButtons;
